import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBaseUrl } from "../../api/client";
import {
  clearStoredPlatformAdminKey,
  fetchPlatformSheet,
  fetchPlatformSummary,
  getStoredPlatformAdminKey,
  savePlatformSheetRow,
  setStoredPlatformAdminKey,
  type PlatformSheetRow,
} from "../../api/platform-admin";
import { ApiClientError } from "../../api/types";
import { isOpsPortalHost, opsPortalPath } from "../../lib/ops-host";

const ROLE_OPTIONS = ["owner", "accountant", "hr", "sales_rep", "warehouse", "ai"];

type StatusFilter = "all" | "paid" | "trial" | "expired";

type EditableRow = PlatformSheetRow & { newPassword: string; dirty: boolean; saving: boolean };

function toEditable(r: PlatformSheetRow): EditableRow {
  return { ...r, newPassword: "", dirty: false, saving: false };
}

function rowPayload(r: EditableRow) {
  return {
    login: r.login,
    role: r.role,
    active: r.active,
    paidUntil: r.paidUntil.trim() ? r.paidUntil.trim() : null,
    trialEnds: r.trialEnds.trim() ? r.trialEnds.trim() : null,
    companyName: r.companyName,
    displayName: r.displayName,
    address: r.address,
    whatsapp: r.whatsapp,
    altEmail: r.altEmail,
    billCom: r.billCom,
    planFull: r.planFull,
    modInv: r.modInv,
    modAcc: r.modAcc,
    modMkt: r.modMkt,
    modHr: r.modHr,
    modFin: r.modFin,
    modSl: r.modSl,
    newPassword: r.newPassword.trim() || undefined,
  };
}

export function PlatformSheetPage() {
  const [adminKey, setAdminKey] = useState(() => getStoredPlatformAdminKey() ?? "");
  const [unlocked, setUnlocked] = useState(() => Boolean(getStoredPlatformAdminKey()));
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    const key = getStoredPlatformAdminKey();
    if (!key) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPlatformSheet(key);
      setRows(data.rows.map(toEditable));
    } catch (err) {
      if (err instanceof ApiClientError && (err.status === 401 || err.status === 503)) {
        clearStoredPlatformAdminKey();
        setUnlocked(false);
      }
      setError(err instanceof Error ? err.message : "فشل التحميل");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) void load();
  }, [unlocked, load]);

  const filtered = useMemo(() => {
    let list = rows;
    if (statusFilter !== "all") {
      list = list.filter((r) => r.subscriptionKind === statusFilter);
    }
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (r) =>
        r.login.toLowerCase().includes(q) ||
        r.companyName.toLowerCase().includes(q) ||
        r.orgCode.toLowerCase().includes(q) ||
        r.displayName.toLowerCase().includes(q),
    );
  }, [rows, filter, statusFilter]);

  function patch(userId: string, patch: Partial<EditableRow>) {
    setRows((prev) =>
      prev.map((r) => (r.userId === userId ? { ...r, ...patch, dirty: true } : r)),
    );
  }

  async function handleSave(userId: string) {
    const key = getStoredPlatformAdminKey();
    if (!key) return;
    const row = rows.find((r) => r.userId === userId);
    if (!row) return;

    setRows((prev) => prev.map((r) => (r.userId === userId ? { ...r, saving: true } : r)));
    setMessage(null);
    setError(null);
    try {
      const { row: saved } = await savePlatformSheetRow(key, userId, rowPayload(row));
      setRows((prev) =>
        prev.map((r) => (r.userId === userId ? { ...toEditable(saved), saving: false } : r)),
      );
      setMessage(`تم حفظ: ${saved.login}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "فشل الحفظ");
      setRows((prev) => prev.map((r) => (r.userId === userId ? { ...r, saving: false } : r)));
    }
  }

  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    const k = adminKey.trim();
    if (!k) return;
    setError(null);
    setLoading(true);
    try {
      setStoredPlatformAdminKey(k);
      await fetchPlatformSummary(k);
      setUnlocked(true);
    } catch (err) {
      clearStoredPlatformAdminKey();
      setUnlocked(false);
      setError(
        err instanceof Error
          ? err.message
          : "فشل التحقق من المفتاح — تأكد من PLATFORM_ADMIN_SECRET على Worker",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div dir="rtl" lang="ar" className="min-h-screen bg-[#e8f5e9] text-slate-900">
      <header className="sticky top-0 z-20 border-b border-emerald-800/20 bg-[#1b5e20] px-4 py-3 text-white shadow">
        <div className="mx-auto flex max-w-[100%] flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold">GoDo — إدارة المستخدمين</h1>
            <p className="text-xs text-emerald-100">
              {isOpsPortalHost() ? "usr.godo-erp.com" : "ops portal"} · تجربة / مدفوع / منتهٍ
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Link to={opsPortalPath("platform")} className="rounded bg-white/15 px-3 py-1 hover:bg-white/25">
              لوحة الملخص
            </Link>
            <a
              href="https://pro.godo-erp.com/login"
              className="rounded bg-white/15 px-3 py-1 hover:bg-white/25"
            >
              ERP (pro)
            </a>
            {unlocked && (
              <button
                type="button"
                className="rounded bg-white/15 px-3 py-1"
                onClick={() => {
                  clearStoredPlatformAdminKey();
                  setUnlocked(false);
                  setRows([]);
                }}
              >
                قفل
              </button>
            )}
          </div>
        </div>
      </header>

      {!unlocked ? (
        <form onSubmit={handleUnlock} className="mx-auto mt-16 max-w-md rounded-lg bg-white p-6 shadow-lg">
          <label className="text-sm font-medium">مفتاح PLATFORM_ADMIN_SECRET</label>
          <input
            type="password"
            className="mt-2 w-full rounded border px-3 py-2 text-sm"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
          />
          <button
            type="submit"
            disabled={loading}
            className="mt-4 w-full rounded bg-[#2e7d32] py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {loading ? "جاري التحقق…" : "فتح الجدول"}
          </button>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <p className="mt-4 text-[11px] text-slate-500" dir="ltr">
            API: {getApiBaseUrl()}
          </p>
        </form>
      ) : (
        <div className="p-3">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <input
              type="search"
              placeholder="بحث: بريد · شركة · org_code"
              className="min-w-[220px] rounded border border-slate-300 bg-white px-3 py-1.5 text-sm"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="rounded bg-[#2e7d32] px-4 py-1.5 text-sm text-white"
            >
              {loading ? "…" : "تحديث"}
            </button>
            <span className="text-xs text-slate-600">{filtered.length} صف</span>
            {(
              [
                ["all", "الكل"],
                ["paid", "مدفوع"],
                ["trial", "تجربة"],
                ["expired", "منتهٍ"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setStatusFilter(id)}
                className={`rounded px-3 py-1 text-xs ${
                  statusFilter === id ? "bg-[#2e7d32] text-white" : "bg-white border border-slate-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {error && <div className="mb-2 rounded bg-red-100 px-3 py-2 text-sm text-red-800">{error}</div>}
          {message && <div className="mb-2 rounded bg-emerald-100 px-3 py-2 text-sm text-emerald-900">{message}</div>}

          {loading && rows.length === 0 && (
            <p className="mb-2 text-sm text-slate-600">جاري تحميل المستخدمين من D1…</p>
          )}
          {!loading && rows.length === 0 && !error && (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
              لا توجد صفوف. تحقق من المفتاح و Worker + D1. ابحث في الجدول عن{" "}
              <span dir="ltr">wael777@gmail.com</span> بعد التحميل.
            </div>
          )}

          <div className="overflow-auto rounded border border-slate-400 bg-white shadow-inner" style={{ maxHeight: "calc(100vh - 140px)" }}>
            <table className="min-w-[2200px] border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-[#c8e6c9] text-[11px] font-semibold">
                <tr>
                  <th className="border border-slate-400 px-1 py-1">حفظ</th>
                  <th className="border border-slate-400 px-2 py-1">حالة</th>
                  <th className="border border-slate-400 px-1 py-1">أيام</th>
                  <th className="border border-slate-400 px-1 py-1">جرّب سابقاً</th>
                  <th className="border border-slate-400 px-2 py-1">login</th>
                  <th className="border border-slate-400 px-2 py-1">role</th>
                  <th className="border border-slate-400 px-1 py-1">active</th>
                  <th className="border border-slate-400 px-2 py-1">paid_until</th>
                  <th className="border border-slate-400 px-2 py-1">trial_ends</th>
                  <th className="border border-slate-400 px-2 py-1">اسم المنشأة</th>
                  <th className="border border-slate-400 px-2 py-1">العنوان</th>
                  <th className="border border-slate-400 px-2 py-1">واتساب</th>
                  <th className="border border-slate-400 px-2 py-1">بريد بديل</th>
                  <th className="border border-slate-400 px-1 py-1">bill_com</th>
                  <th className="border border-slate-400 px-1 py-1">plan_full</th>
                  <th className="border border-slate-400 px-1 py-1">mod_inv</th>
                  <th className="border border-slate-400 px-1 py-1">mod_acc</th>
                  <th className="border border-slate-400 px-1 py-1">mod_mkt</th>
                  <th className="border border-slate-400 px-1 py-1">mod_hr</th>
                  <th className="border border-slate-400 px-1 py-1">mod_fin</th>
                  <th className="border border-slate-400 px-1 py-1">mod_sl</th>
                  <th className="border border-slate-400 px-2 py-1">كلمة سر جديدة</th>
                  <th className="border border-slate-400 px-2 py-1">org_id</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.userId}
                    className={
                      r.subscriptionKind === "expired"
                        ? "bg-red-50"
                        : r.subscriptionKind === "paid"
                          ? "bg-[#c8e6c9]"
                          : r.subscriptionKind === "trial"
                            ? "bg-[#fff9c4]"
                            : "bg-[#e8f5e9]"
                    }
                  >
                    <td className="border border-slate-300 p-1 text-center">
                      <button
                        type="button"
                        disabled={r.saving}
                        onClick={() => void handleSave(r.userId)}
                        className={`rounded px-2 py-0.5 text-[10px] font-bold text-white ${
                          r.dirty ? "bg-amber-600" : "bg-[#388e3c]"
                        }`}
                      >
                        {r.saving ? "…" : r.dirty ? "!" : "✓"}
                      </button>
                    </td>
                    <td className="border border-slate-300 px-1 py-0.5 text-center font-semibold">
                      {r.subscriptionLabel}
                    </td>
                    <td className="border border-slate-300 px-1 py-0.5 text-center">{r.daysRemaining}</td>
                    <td className="border border-slate-300 px-1 py-0.5 text-center">
                      {r.trialEmailRegistered ? "نعم" : "—"}
                    </td>
                    <td className="border border-slate-300 p-0">
                      <input
                        dir="ltr"
                        className="w-full min-w-[140px] bg-transparent px-1 py-0.5"
                        value={r.login}
                        onChange={(e) => patch(r.userId, { login: e.target.value })}
                      />
                    </td>
                    <td className="border border-slate-300 p-0">
                      <select
                        className="w-full bg-transparent px-1 py-0.5"
                        value={r.role}
                        onChange={(e) => patch(r.userId, { role: e.target.value })}
                      >
                        {ROLE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="border border-slate-300 p-0 text-center">
                      <input
                        type="checkbox"
                        checked={r.active}
                        onChange={(e) => patch(r.userId, { active: e.target.checked })}
                      />
                    </td>
                    <td className="border border-slate-300 p-0">
                      <input
                        dir="ltr"
                        className="w-full min-w-[120px] bg-transparent px-1 py-0.5"
                        value={r.paidUntil}
                        placeholder="dd/mm/yyyy HH:mm"
                        onChange={(e) => patch(r.userId, { paidUntil: e.target.value })}
                      />
                    </td>
                    <td className="border border-slate-300 p-0">
                      <input
                        dir="ltr"
                        className="w-full min-w-[120px] bg-transparent px-1 py-0.5"
                        value={r.trialEnds}
                        onChange={(e) => patch(r.userId, { trialEnds: e.target.value })}
                      />
                    </td>
                    <td className="border border-slate-300 p-0">
                      <input
                        className="w-full min-w-[160px] bg-transparent px-1 py-0.5"
                        value={r.companyName}
                        onChange={(e) => patch(r.userId, { companyName: e.target.value })}
                      />
                    </td>
                    <td className="border border-slate-300 p-0">
                      <input
                        className="w-full min-w-[100px] bg-transparent px-1 py-0.5"
                        value={r.address}
                        onChange={(e) => patch(r.userId, { address: e.target.value })}
                      />
                    </td>
                    <td className="border border-slate-300 p-0">
                      <input
                        dir="ltr"
                        className="w-full min-w-[100px] bg-transparent px-1 py-0.5"
                        value={r.whatsapp}
                        onChange={(e) => patch(r.userId, { whatsapp: e.target.value })}
                      />
                    </td>
                    <td className="border border-slate-300 p-0">
                      <input
                        dir="ltr"
                        className="w-full min-w-[120px] bg-transparent px-1 py-0.5"
                        value={r.altEmail}
                        onChange={(e) => patch(r.userId, { altEmail: e.target.value })}
                      />
                    </td>
                    {(
                      [
                        ["billCom", r.billCom],
                        ["planFull", r.planFull],
                        ["modInv", r.modInv],
                        ["modAcc", r.modAcc],
                        ["modMkt", r.modMkt],
                        ["modHr", r.modHr],
                        ["modFin", r.modFin],
                        ["modSl", r.modSl],
                      ] as const
                    ).map(([key, val]) => (
                      <td key={key} className="border border-slate-300 p-0 text-center">
                        <input
                          type="checkbox"
                          checked={val}
                          onChange={(e) => patch(r.userId, { [key]: e.target.checked })}
                        />
                      </td>
                    ))}
                    <td className="border border-slate-300 p-0">
                      <input
                        type="password"
                        dir="ltr"
                        placeholder="اختياري"
                        className="w-full min-w-[90px] bg-transparent px-1 py-0.5"
                        value={r.newPassword}
                        onChange={(e) => patch(r.userId, { newPassword: e.target.value })}
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-0.5 font-mono text-[10px]" dir="ltr">
                      {r.orgId}
                      {r.ownerFlag ? " · owner" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-[11px] text-slate-600">
            <strong>تحرير:</strong> غيّر الخلية → زر <strong>!</strong> برتقالي → اضغط للحفظ في D1.
            paid_until / trial_ends على مستوى المنظمة. API:{" "}
            <span dir="ltr">{getApiBaseUrl()}</span>
          </p>
        </div>
      )}
    </div>
  );
}
